import React, { useEffect, useRef } from "react";
import styled from "styled-components";

interface OtpCodeInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  onSubmit?: (code?: string) => void;
  autoFocus?: boolean;
}

const OtpRow = styled.div`
  display: flex;
  gap: 0.72rem;
  justify-content: center;
`;

const DigitInput = styled.input`
  width: 2.95rem;
  height: 3.1rem;
  text-align: center;
  border: 1px solid #cbd5e1;
  border-radius: 11px;
  font-size: 1.18rem;
  font-weight: 600;
  color: #111827;
  background: #ffffff;
  transition: border-color 150ms ease-in-out, box-shadow 150ms ease-in-out;

  &:focus-visible {
    outline: 0;
    border-color: #64748b;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.24);
  }

  &:disabled {
    opacity: 0.72;
    cursor: not-allowed;
  }
`;

const OTP_LEN = 6;

const OtpCodeInput: React.FC<OtpCodeInputProps> = ({
  value,
  onChange,
  disabled,
  onSubmit,
  autoFocus = false,
}) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    refs.current[0]?.focus();
  }, [autoFocus, disabled]);

  const updateDigit = (index: number, raw: string) => {
    const normalized = raw.replace(/\D/g, "").slice(-1);
    const next = [...value];
    next[index] = normalized;
    onChange(next);

    if (normalized && index < OTP_LEN - 1) {
      refs.current[index + 1]?.focus();
    }

    if (onSubmit && next.every((digit) => digit.length === 1)) {
      onSubmit(next.join(""));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && value[index] === "" && index > 0) {
      refs.current[index - 1]?.focus();
      return;
    }

    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
      return;
    }

    if (e.key === "ArrowRight" && index < OTP_LEN - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit?.(value.join(""));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;

    const next = new Array(OTP_LEN).fill("").map((_, idx) => pasted[idx] || "");
    onChange(next);

    const lastIndex = Math.min(OTP_LEN - 1, pasted.length - 1);
    if (lastIndex >= 0) {
      refs.current[lastIndex]?.focus();
    }

    if (onSubmit && next.every((digit) => digit.length === 1)) {
      onSubmit(next.join(""));
    }
  };

  return (
    <OtpRow onPaste={handlePaste}>
      {value.map((digit, index) => (
        <DigitInput
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          aria-label={`OTP digit ${index + 1}`}
          onChange={(e) => updateDigit(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onFocus={(e) => e.currentTarget.select()}
          disabled={disabled}
        />
      ))}
    </OtpRow>
  );
};

export default OtpCodeInput;
